// frontend/src/components/dashboard/TabelaVendas.jsx
import React, { useMemo, useState } from 'react';
import { formatBRL } from './mockData';
import { useAuth } from '../../context/AuthContext';
import { criarVenda, atualizarVenda } from '../../services/vendasService';
import { FaEdit, FaPlus } from 'react-icons/fa';

/* =======================
   AURORA (dark)
======================= */
const AURORA = {
  border: 'rgba(255,255,255,0.10)',
  text: 'rgba(255,255,255,0.92)',
  text2: 'rgba(255,255,255,0.65)',
  text3: 'rgba(255,255,255,0.45)',
  cardBg: 'rgba(9,9,11,0.80)',
  emerald: '#34D399',
};

function formatDate(d) {
  if (!d) return '—';

  if (typeof d === 'object' && typeof d.toDate === 'function') {
    const dt = d.toDate();
    if (!Number.isNaN(dt.getTime())) return formatDate(dt);
  }

  if (typeof d === 'string' && d.includes('-')) {
    const parts = d.split('-');
    if (parts.length === 3) {
      const [ano, mes, dia] = parts;
      return `${dia}/${mes}/${ano}`;
    }
  }

  if (!(d instanceof Date) || isNaN(d)) return '—';

  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const ano = d.getFullYear();
  return `${dia}/${mes}/${ano}`;
}

function parseDateBrToIso(brDate) {
  if (!brDate || brDate === '—') return '';
  const parts = brDate.split('/');
  if (parts.length !== 3) return '';
  const [dia, mes, ano] = parts;
  return `${ano}-${mes}-${dia}`;
}

function toDateSafe(raw) {
  if (!raw) return null;

  if (typeof raw === 'object' && typeof raw.toDate === 'function') {
    const d = raw.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;

  if (typeof raw === 'string') {
    const s = raw.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const d = new Date(`${s}T12:00:00`);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(s.includes('T') ? s : `${s}T12:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * ✅ Presentational (alinhado ao Dashboard):
 * Props:
 * - vendas: array (docs do firestore) já filtradas por ano
 * - loading: boolean
 * - year: number (ex: 2026)
 * - rightSlot: ReactNode (ex: filtro de ano)
 * - onSaved: callback para o Dashboard recarregar o financeiro do ano
 */
export default function TabelaVendas({
  vendas = [],
  loading = false,
  year,
  rightSlot = null,
  onSaved,
}) {
  const { usuario } = useAuth();

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState({
    data: '', // YYYY-MM-DD
    nome: '',
    produto: '',
    valor: '',
    comissao: '',
  });

  const rows = useMemo(() => {
    const items = Array.isArray(vendas) ? vendas : [];
    return items.map((v) => {
      const rawDate = v.data || v.dataVenda;
      const finalDate = toDateSafe(rawDate);

      return {
        id: v.id,
        data: formatDate(finalDate),
        nome: v.clienteNome || '—',
        produto: v.produtoDescricao || v.produtoTipo || '—',
        valor: Number(v.valorVenda ?? v.valor ?? 0),
        comissao: Number(v.comissao ?? 0),
      };
    });
  }, [vendas]);

  const totalPeriodo = useMemo(
    () => rows.reduce((acc, r) => acc + (Number(r.comissao) || 0), 0),
    [rows]
  );

  const handleEdit = (row) => {
    setEditId(row.id);
    setForm({
      data: parseDateBrToIso(row.data),
      nome: row.nome === '—' ? '' : row.nome,
      produto: row.produto === '—' ? '' : row.produto,
      valor: row.valor,
      comissao: row.comissao,
    });
    setOpen(true);
  };

  const handleNew = () => {
    setEditId(null);
    setForm({ data: '', nome: '', produto: '', valor: '', comissao: '' });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!usuario?.uid) return;

    try {
      setSaving(true);

      // ✅ ALINHADO AO vendasService:
      // - manda dataVenda (string YYYY-MM-DD)
      // - manda produtoDescricao (campo atual)
      // - manda valor/comissao como string (service faz parse pt-BR)
      const payload = {
        clienteNome: form.nome,
        corretorNome: usuario.nome || 'Corretor',
        produtoDescricao: form.produto,
        valorVenda: form.valor,
        comissao: form.comissao,
        dataVenda: form.data,
        origem: 'manual',
      };

      if (editId) await atualizarVenda(editId, payload);
      else await criarVenda(usuario.uid, payload);

      setOpen(false);
      setEditId(null);
      setForm({ data: '', nome: '', produto: '', valor: '', comissao: '' });

      // ✅ pede pro Dashboard recarregar (ano atual)
      if (typeof onSaved === 'function') onSaved();
    } catch (err) {
      console.error('Erro ao salvar venda:', err);
      alert('Não foi possível salvar a venda. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="relative overflow-hidden rounded-3xl border p-5 shadow-2xl backdrop-blur"
      style={{ borderColor: AURORA.border, background: AURORA.cardBg }}
    >
      {/* halos */}
      <div
        className="pointer-events-none absolute -right-28 -top-28 h-80 w-80 rounded-full blur-3xl"
        style={{ background: 'rgba(167,139,250,0.18)' }}
      />
      <div
        className="pointer-events-none absolute -left-28 -bottom-28 h-80 w-80 rounded-full blur-3xl"
        style={{ background: 'rgba(52,211,153,0.14)' }}
      />

      <div className="relative">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <div
                className="text-base font-semibold"
                style={{ color: AURORA.text }}
              >
                Vendas fechadas
              </div>

              {year ? (
                <span
                  className="rounded-full border px-2 py-0.5 text-[10px] font-extrabold"
                  style={{
                    borderColor: AURORA.border,
                    background: 'rgba(255,255,255,0.06)',
                    color: AURORA.text2,
                  }}
                >
                  Ano {year}
                </span>
              ) : null}
            </div>

            <div className="text-xs" style={{ color: AURORA.text2 }}>
              Histórico anual do corretor (tabela)
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* ✅ slot (filtro de ano) */}
            {rightSlot}

            <button
              onClick={handleNew}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-3 py-2 text-xs font-bold text-[#06131f] hover:bg-emerald-400 active:scale-[0.99]"
              title="Registrar venda"
            >
              <FaPlus />
              Venda fechada
            </button>
          </div>
        </div>

        <div
          className="overflow-auto rounded-2xl border"
          style={{
            borderColor: AURORA.border,
            background: 'rgba(255,255,255,0.03)',
          }}
        >
          <table className="w-full text-[13px]">
            <thead
              className="sticky top-0 z-10 backdrop-blur"
              style={{
                background: 'rgba(9,9,11,0.88)',
                borderBottom: `1px solid ${AURORA.border}`,
              }}
            >
              <tr className="text-left" style={{ color: AURORA.text2 }}>
                <th className="px-3 py-2 font-semibold">Data</th>
                <th className="px-3 py-2 font-semibold">Cliente</th>
                <th className="px-3 py-2 font-semibold">Produto</th>
                <th className="px-3 py-2 font-semibold">Valor</th>
                <th className="px-3 py-2 font-semibold">Comissão</th>
                <th className="px-3 py-2 w-10" />
              </tr>
            </thead>

            <tbody
              className="[&_tr]:border-t"
              style={{ color: AURORA.text, borderColor: AURORA.border }}
            >
              {loading && (
                <tr>
                  <td
                    className="px-3 py-3"
                    colSpan={6}
                    style={{ color: AURORA.text2 }}
                  >
                    Carregando vendas…
                  </td>
                </tr>
              )}

              {!loading &&
                rows.map((r, idx) => (
                  <tr
                    key={r.id}
                    className="group"
                    style={{
                      background:
                        idx % 2 === 0
                          ? 'rgba(255,255,255,0.00)'
                          : 'rgba(255,255,255,0.03)',
                      borderColor: AURORA.border,
                    }}
                  >
                    <td className="px-3 py-2" style={{ color: AURORA.text }}>
                      {r.data}
                    </td>
                    <td className="px-3 py-2" style={{ color: AURORA.text }}>
                      {r.nome}
                    </td>
                    <td className="px-3 py-2" style={{ color: AURORA.text2 }}>
                      {r.produto}
                    </td>
                    <td className="px-3 py-2" style={{ color: AURORA.text2 }}>
                      {formatBRL(r.valor)}
                    </td>
                    <td
                      className="px-3 py-2 font-semibold"
                      style={{ color: AURORA.emerald }}
                    >
                      {formatBRL(r.comissao)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => handleEdit(r)}
                        className="rounded-lg p-1.5 transition-colors"
                        style={{ color: AURORA.text3 }}
                        title="Editar"
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = AURORA.emerald)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.color = AURORA.text3)
                        }
                      >
                        <FaEdit />
                      </button>
                    </td>
                  </tr>
                ))}

              {!loading && rows.length === 0 && (
                <tr>
                  <td
                    className="px-3 py-3"
                    colSpan={6}
                    style={{ color: AURORA.text2 }}
                  >
                    Sem vendas no período.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-2 text-xs" style={{ color: AURORA.text2 }}>
          Valor do período (Comissões){year ? ` — ${year}` : ''}:{' '}
          <span className="font-semibold" style={{ color: AURORA.text }}>
            {formatBRL(totalPeriodo)}
          </span>
        </div>

        {open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-2xl border bg-white p-4 shadow-xl">
              <div className="mb-3 text-base font-semibold text-zinc-900">
                {editId ? 'Editar Venda' : 'Nova Venda'}
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-zinc-500">Data</label>
                    <input
                      type="date"
                      value={form.data}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, data: e.target.value }))
                      }
                      className="mt-1 w-full rounded-lg border bg-transparent px-3 py-2 text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="text-xs text-zinc-500">Cliente</label>
                    <input
                      value={form.nome}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, nome: e.target.value }))
                      }
                      className="mt-1 w-full rounded-lg border bg-transparent px-3 py-2 text-sm"
                      placeholder="Nome"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-zinc-500">Produto</label>
                  <input
                    value={form.produto}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, produto: e.target.value }))
                    }
                    className="mt-1 w-full rounded-lg border bg-transparent px-3 py-2 text-sm"
                    placeholder="Ex.: Casa 3Q — Bairro"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-zinc-500">
                      Valor Total (R$)
                    </label>
                    <input
                      type="text"
                      value={form.valor}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, valor: e.target.value }))
                      }
                      className="mt-1 w-full rounded-lg border bg-transparent px-3 py-2 text-sm"
                      placeholder="Ex.: 350000 ou 350.000,00"
                      required
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-emerald-700">
                      Comissão (R$)
                    </label>
                    <input
                      type="text"
                      value={form.comissao}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, comissao: e.target.value }))
                      }
                      className="mt-1 w-full rounded-lg border border-emerald-200 bg-emerald-50/30 px-3 py-2 text-sm"
                      placeholder="Ex.: 15000 ou 15.000,00"
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-lg border px-3 py-2 text-sm hover:bg-zinc-50"
                    disabled={saving}
                  >
                    Cancelar
                  </button>

                  <button
                    type="submit"
                    className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
                    disabled={saving}
                  >
                    {saving ? 'Salvando...' : editId ? 'Atualizar' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
